import * as pc from 'playcanvas';
import { t } from '../i18n';
import type { WorldMap } from '../api/types';

/**
 * Floating DOM name labels above the semantic buildings — the product moment
 * where a school/company sees its own name on its own building. DOM (not 3D
 * text) so Arabic/RTL renders natively crisp. Labels fade with distance and
 * hide behind the camera; pointer-events stay off so they never block input.
 */

const MAX_DISTANCE = 150;

interface LabelItem {
  id: string;
  el: HTMLDivElement;
  anchor: pc.Vec3;
}

export class BuildingLabels {
  private readonly items: LabelItem[] = [];
  private readonly tmp = new pc.Vec3();
  private readonly toAnchor = new pc.Vec3();
  private highlighted: string | null = null;

  constructor(
    map: WorldMap,
    private readonly camera: pc.Entity,
    private readonly canvas: HTMLCanvasElement,
  ) {
    const host = document.createElement('div');
    host.id = 'labels';
    document.getElementById('hud')!.appendChild(host);

    for (const b of map.buildings) {
      if (b.type === 'npc') continue; // EVA is a character, not architecture
      const el = document.createElement('div');
      el.className = 'bldg-label' + (b.locked ? ' bldg-label-locked' : '');
      el.textContent = b.locked ? `🔒 ${b.nameAr} — ${t('locked_building')}` : b.nameAr;
      host.appendChild(el);
      this.items.push({
        id: b.id,
        el,
        anchor: new pc.Vec3(b.position.x, b.size.y + 2.4, b.position.z),
      });
    }
  }

  /** Pulses the label of the building the player can interact with right now. */
  setHighlight(id: string | null): void {
    if (id === this.highlighted) return;
    for (const item of this.items) {
      item.el.classList.toggle('bldg-label-active', item.id === id);
    }
    this.highlighted = id;
  }

  update(): void {
    const cam = this.camera.camera;
    if (!cam) return;
    const camPos = this.camera.getPosition();
    const forward = this.camera.forward;
    // worldToScreen returns canvas-resolution pixels; CSS needs client pixels.
    const scaleX = this.canvas.clientWidth / this.canvas.width;
    const scaleY = this.canvas.clientHeight / this.canvas.height;

    for (const item of this.items) {
      const dist = camPos.distance(item.anchor);
      this.toAnchor.sub2(item.anchor, camPos);
      const inFront = this.toAnchor.dot(forward) > 0;
      if (!inFront || dist > MAX_DISTANCE) {
        item.el.style.display = 'none';
        continue;
      }
      cam.worldToScreen(item.anchor, this.tmp);
      item.el.style.display = 'block';
      item.el.style.transform = `translate(-50%, -100%) translate(${this.tmp.x * scaleX}px, ${this.tmp.y * scaleY}px)`;
      item.el.style.opacity = String(pc.math.clamp(1.5 - dist / MAX_DISTANCE, 0.3, 1));
    }
  }
}
